import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { updateSubcontract } from '@/modules/Contract/modules/Subcontract/services'
import {
    Subcontract,
    subcontractStatus,
} from '@/modules/Contract/modules/Subcontract/types'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import {
    formatDateWithFormatter,
    isAttachment,
    renderFileItem,
} from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { schemaUpdateSubcontractModal } from './schema'
import ServiceCustomerSelect from '@/components/Form/Shared/ServiceCustomerSelect'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface UpdateSubcontractFormValues {
    subcontract: Omit<
        Subcontract,
        'subcontract_id' | 'employee_id' | 'contract_id' | 'service_id'
    >
    attachments?: File[] | Attachment[]
}
export interface UpdateSubcontractModalProps extends ModalProps {
    contractID: number
    subcontractID: number
    subcontract: Subcontract
    contractType?: 'customer' | 'partner'
}

const UpdateSubcontractModal = ({
    onClose,
    open,
    contractID,
    subcontractID,
    contractType = 'customer',
    subcontract,
}: UpdateSubcontractModalProps) => {
    const { refetch } = useQueryClientCallback({
        pattern: 'subcontracts',
        mode: 'contains',
    })
    const { t } = useTranslation()

    const formControl = useForm<UpdateSubcontractFormValues>({
        defaultValues: {
            attachments: subcontract?.attachments ?? [],
            subcontract: {
                subcontract_code: subcontract?.subcontract_code,
                subcontract_name: subcontract.subcontract_name,
                subcontract_effective_date:
                    subcontract?.subcontract_effective_date,
                subcontract_expiry_date: subcontract?.subcontract_expiry_date,
                subcontract_status: subcontract.subcontract_status,
                // Seed from the junction-derived services[], not from subcontracts.service_ids:
                // that array is a denormalised shadow that has drifted, and the backend now treats
                // an explicit [] as "detach everything".
                service_ids: (subcontract?.services ?? []).map(
                    (service) => service.service_id
                ),
            },
        },
        resolver: yupResolver(schemaUpdateSubcontractModal as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateSubcontract,
        },
        {
            successMessage: t('update-subcontract-success'),
            reactQueryKeys: QUERY_KEYS.SUBCONTRACTS,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const storagePrefix =
        contractType === 'partner'
            ? STORGE_PREFIXS.SUBCONTRACT_PARTNER
            : STORGE_PREFIXS.SUBCONTRACT

    const { isUploading, uploadAsync } = useUploadFile({
        prefix: storagePrefix,
    })

    const onSubmit = async (values: UpdateSubcontractFormValues) => {
        let attachments: Attachment[] = []
        let attachments_to_delete: number[] = []
        if (values?.attachments && values?.attachments?.length > 0) {
            const attFiles = values?.attachments?.filter(
                (attachment) => attachment instanceof File
            ) as File[]
            const atts = values?.attachments?.filter((attachment) =>
                isAttachment(attachment)
            ) as Attachment[]
            const attIDs = atts?.map(
                (attachment) => attachment.attachment_id
            ) as number[]

            attachments_to_delete = subcontract?.attachments
                ?.filter(
                    (attachment) => !attIDs.includes(attachment.attachment_id)
                )
                ?.map((attachment) => attachment.attachment_id) as number[]
            const attachmentUploadeds = await uploadAsync(attFiles)

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]
        }

        mutateAsync({
            contractType,
            contractID,
            subcontractID,
            data: {
                subcontract: {
                    ...values.subcontract,
                    subcontract_effective_date: values.subcontract
                        .subcontract_effective_date
                        ? formatDateWithFormatter(
                              values.subcontract.subcontract_effective_date,
                              'YYYY-MM-DD'
                          )
                        : null,
                    subcontract_expiry_date: values.subcontract
                        .subcontract_expiry_date
                        ? formatDateWithFormatter(
                              values.subcontract.subcontract_expiry_date,
                              'YYYY-MM-DD'
                          )
                        : null,
                },
                attachments,
                attachments_to_delete,
            },
        })
    }

    const onDelete = (file: File | Attachment) => {
        const isFile = file instanceof File
        if (isFile) {
            const attachments = formControl.getValues('attachments')
            const newAttachments = attachments?.filter((attachment) => {
                if (attachment instanceof File) {
                    return attachment.name !== file.name
                }
                return true
            }) as File[]
            formControl.setValue('attachments', newAttachments)
            return
        }
        if (isAttachment(file)) {
            const attachments = formControl.getValues('attachments')
            const newAttachments = attachments?.filter((attachment) => {
                if (attachment instanceof File) {
                    return true
                }
                return attachment.attachment_id !== file.attachment_id
            }) as Attachment[]
            formControl.setValue('attachments', newAttachments)
        }
        return
    }

    return (
        <Dialog title={t('update-subcontract')} onClose={onClose} open={open}>
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isUploading || isPending,
                    },
                    {
                        title: t('submit'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isUploading || isPending,
                        disabled: isUploading || isPending,
                    },
                ]}
                onSubmit={onSubmit}
                data={[
                    {
                        formItemProps: {
                            // Read-only: UpdateSubcontract has no subcontract_code param, so the
                            // backend has always silently dropped edits made here.
                            name: 'subcontract.subcontract_code',
                            type: 'textfield',
                            label: t('subcontract_code'),
                            placeholder: t('subcontract_code'),
                            disabled: true,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_name',
                            type: 'textfield',
                            label: t('subcontract_name'),
                            placeholder: t('subcontract_name'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_effective_date',
                            type: 'datepicker',
                            label: t('subcontract_effective_date'),
                            placeholder: t('subcontract_effective_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_expiry_date',
                            type: 'datepicker',
                            label: t('subcontract_expiry_date'),
                            placeholder: t('subcontract_expiry_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: (control) => {
                            return (
                                <ServiceCustomerSelect
                                    multiple
                                    eligibleForSubcontract
                                    currentSubcontractID={subcontractID}
                                    label={t('subcontracts.service_id')}
                                    name="subcontract.service_ids"
                                    control={control}
                                    customerID={Number(
                                        subcontract?.customer_id
                                    )}
                                />
                            )
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_status',
                            type: 'select',
                            options: subcontractStatus(t),
                            label: t('subcontract_status'),
                            placeholder: t('subcontract_status'),
                            allowClear: false,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_note',
                            type: 'textarea',
                            label: t('subcontract_note'),
                            placeholder: t('subcontract_note'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'attachments',
                            type: 'files',
                            renderFileItem: (file: File | Attachment) =>
                                renderFileItem(file, onDelete),
                            label: t('current-attachments'),
                            multiple: true,
                            disabled: true,
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default UpdateSubcontractModal
